import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { membershipExistsByEmail } from '#src/app/shared/api/payments';
import { useLoginState } from '#src/app/shared/hooks/session';

/**
 * Returns a callback that — for guest sessions only — checks whether the email
 * the current user just entered in the payment form already belongs to an
 * existing membership.
 *
 * For non-guest sessions the callback short-circuits to `false`. The underlying
 * `membershipExistsByEmail` GraphQL query does an OR across the current
 * session's user id/uuid AND the supplied email, so calling it for logged-in
 * users would conflate "this email's owner is a member" with "the caller is
 * already a member" — and the latter is already known from session/page data,
 * so this hook has nothing useful to add for them.
 *
 * `currentUserEmail` is expected to be the email the current user just entered;
 * the GQL query treats it as a complement to the session lookup, not a
 * free-form third-party email lookup.
 */
export function useIsGuestEmailExistingMember(): (currentUserEmail: string) => Promise<boolean> {
	const utilityContext = useUtilityContext();
	const loginState = useLoginState();
	return async function isGuestEmailExistingMember(currentUserEmail): Promise<boolean> {
		if (loginState !== 'GUEST') {
			return false;
		}
		return membershipExistsByEmail({ email: currentUserEmail }, utilityContext);
	};
}
